import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Menu,
  Dropdown,
  Pagination,
  Dimmer,
  Loader,
  Label,
  Icon
} from "semantic-ui-react";
import {
  statusToKorean,
  reqTypeToKorean,
  decodeEntities
} from "../../../common/utils/format";

const columns = [
  "",
  "신청년월",
  "신청 구분",
  "도서명",
  "묶음",
  "신청일",
  "요청자",
  "상태"
];

// 도서명을 가장 크게, 한글 헤더(신청년월/신청 구분/신청일/요청자/상태)는
// 줄바꿈 없이 한 줄로 보이도록 적당히 넓게.
// 컬럼 순서: ["", 신청년월, 신청 구분, 도서명, 묶음, 신청일, 요청자, 상태]
const columnWidthPercents = [
  "4%",   // 번호
  "9%",   // 신청년월
  "9%",   // 신청 구분
  "46%",  // 도서명 (가장 큰 비중)
  "6%",   // 묶음
  "10%",  // 신청일
  "8%",   // 요청자
  "8%"    // 상태
];

// 도서명 외 셀은 줄바꿈 차단 + 넘치면 ellipsis.
const NOWRAP_STYLE = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

// 묶음별 일관된 색을 자동 할당 (semantic-ui Label color 풀에서 순환)
const BATCH_COLORS = [
  "blue",
  "teal",
  "violet",
  "olive",
  "orange",
  "pink",
  "brown"
];

// row 좌측 colored border 강조용 hex (위 BATCH_COLORS 와 1:1).
const BATCH_BORDER_HEX = {
  blue: "#2185d0",
  teal: "#00b5ad",
  violet: "#6435c9",
  olive: "#b5cc18",
  orange: "#f2711c",
  pink: "#e03997",
  brown: "#a5673f"
};

// 같은 색의 옅은 배경(row 전체 톤). rgba 알파 낮춰서.
const BATCH_BG_RGBA = {
  blue: "rgba(33,133,208,0.08)",
  teal: "rgba(0,181,173,0.08)",
  violet: "rgba(100,53,201,0.08)",
  olive: "rgba(181,204,24,0.10)",
  orange: "rgba(242,113,28,0.08)",
  pink: "rgba(224,57,151,0.08)",
  brown: "rgba(165,103,63,0.08)"
};

const timestampToDate = dateTime => {
  return new Date(dateTime).toLocaleDateString();
};

const options = [
  {
    key: "10",
    text: "10",
    value: "10"
  },
  {
    key: "20",
    text: "20",
    value: "20"
  },
  {
    key: "40",
    text: "40",
    value: "40"
  },
  {
    key: "100",
    text: "100",
    value: "100"
  }
];

const BookListTable = ({ onTableRowClick, books, Loading }) => {
  const [active, setActive] = useState(1);
  const [firstIndex, setFirstIndex] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [pageLastCount, setPageLastCount] = useState(1);
  const [count, setCount] = useState(10);

  // 묶음 정보: batchNum → { count, colorIndex }
  const batchInfo = useMemo(() => {
    const map = {};
    let colorIndex = 0;
    (books || []).forEach(b => {
      if (!b.batchNum) return;
      if (!map[b.batchNum]) {
        map[b.batchNum] = { count: 0, colorIndex: colorIndex++ };
      }
      map[b.batchNum].count += 1;
    });
    return map;
  }, [books]);

  const onPaginationChange = useCallback(
    (e, { activePage }) => {
      setActive({ activePage }.activePage);
      setFirstIndex(Number(activePage) * count - count);
    },
    [count]
  );

  const onCountChange = useCallback((e, { value }) => {
    setCount(Number({ value }.value));
    setActive(1);
    setFirstIndex(0);
  }, []);

  useEffect(() => {
    if (books) {
      setTotalPage(Number(Math.ceil(books.length / count)));

      if (Number(totalPage) === Number(active)) {
        setPageLastCount(books.length);
      } else {
        setPageLastCount(firstIndex + count);
      }
    }
  }, [books, totalPage, active, pageLastCount, firstIndex, count]);

  useEffect(() => {
    setActive(1);
    setFirstIndex(0);
  }, [books]);

  return (
    <>
      <Menu compact>
        <Dropdown
          compact
          selection
          defaultValue={options[0].value}
          onChange={onCountChange}
          options={options}
        />
      </Menu>
      {Loading && (
        <Dimmer active inverted>
          <Loader>Loading...</Loader>
        </Dimmer>
      )}

      {/*
        모바일에서 표가 좁아져 컬럼이 깨지는 걸 막기 위해
        가로 스크롤 컨테이너 + Table 에 minWidth.
        - PC (≥ 900px): 컨테이너가 더 넓어 스크롤 안 생김, 그대로 표시.
        - 모바일 (< 900px): Table 이 minWidth 유지, 좌우 스크롤로 표 전체 가로로 볼 수 있음.
        unstackable: semantic-ui 기본의 모바일 stacked 동작(행이 세로로 풀림) 비활성화.
      */}
      <div style={{ overflowX: "auto" }}>
      <Table fixed celled striped selectable unstackable style={{ minWidth: 900 }}>
        {/*
          table-layout: fixed 인 경우 컬럼 width 는 첫 row 의 cell width 또는 colgroup 으로 결정.
          헤더 colSpan 때문에 첫 row 에 width 못 줘서 colgroup 으로 강제.
        */}
        <colgroup>
          {columnWidthPercents.map((w, idx) => (
            <col key={idx} style={{ width: w }} />
          ))}
        </colgroup>
        <Table.Header>
          <Table.Row textAlign={"center"}>
            <Table.HeaderCell colSpan="7">
              <Pagination
                pointing
                secondary
                activePage={active}
                totalPages={totalPage}
                onPageChange={onPaginationChange}
              />
            </Table.HeaderCell>
            <Table.HeaderCell colSpan="1">
              {books != null && books.length > 0 ? (
                <>
                  {firstIndex + 1}-{pageLastCount} / {books.length}
                </>
              ) : (
                `0 / 0`
              )}
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            {columns.map((column, idx) => (
              <Table.HeaderCell
                key={column}
                textAlign="center"
                style={{ width: columnWidthPercents[idx], whiteSpace: "nowrap" }}
              >
                {column}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {books && books.length > 0 ? (
            books.map((book, index) =>
              index < firstIndex + count && index >= firstIndex ? (
                (() => {
                  const batch = book.batchNum && batchInfo[book.batchNum];
                  const inMultiBatch = batch && batch.count >= 2;
                  const batchColor = inMultiBatch
                    ? BATCH_COLORS[batch.colorIndex % BATCH_COLORS.length]
                    : null;
                  // 모바일에선 더블클릭이 불가능해 단일 클릭으로 상세 진입.
                  // cursor:pointer 로 클릭 가능함을 힌트.
                  const rowStyle = inMultiBatch
                    ? {
                        cursor: "pointer",
                        borderLeft: `5px solid ${BATCH_BORDER_HEX[batchColor]}`,
                        backgroundColor: BATCH_BG_RGBA[batchColor]
                      }
                    : { cursor: "pointer" };
                  return (
                <Table.Row
                  key={book.ordNum}
                  className="dbrs-book-row"
                  onClick={e => {
                    // 텍스트 드래그 선택(복사)일 땐 상세로 이동하지 않음.
                    if (window.getSelection && String(window.getSelection())) return;
                    onTableRowClick(book.ordNum);
                  }}
                  style={rowStyle}
                >
                  <Table.Cell style={NOWRAP_STYLE}>{index + 1}</Table.Cell>
                  <Table.Cell style={NOWRAP_STYLE}>
                    {convertDateFormatForMonth(book.reqYearMonth)}
                  </Table.Cell>
                  <Table.Cell style={NOWRAP_STYLE}>
                    {reqTypeToKorean(book.reqType)}
                  </Table.Cell>
                  <Table.Cell>
                    {inMultiBatch ? (
                      <Icon
                        name="linkify"
                        style={{
                          color: BATCH_BORDER_HEX[batchColor],
                          marginRight: 6
                        }}
                        title={`묶음 ${batch.count}권`}
                      />
                    ) : null}
                    {decodeEntities(book.bookName)}
                  </Table.Cell>
                  <Table.Cell textAlign="center" style={NOWRAP_STYLE}>
                    {(() => {
                      const info = book.batchNum && batchInfo[book.batchNum];
                      if (!info || info.count < 2) return null;
                      const color = BATCH_COLORS[info.colorIndex % BATCH_COLORS.length];
                      return (
                        <Label color={color} size="mini">
                          묶음 {info.count}권
                        </Label>
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell style={NOWRAP_STYLE}>
                    {timestampToDate(book.regDt)}
                  </Table.Cell>
                  <Table.Cell style={NOWRAP_STYLE}>{book.mbrName}</Table.Cell>
                  <Table.Cell style={NOWRAP_STYLE}>
                    {statusToKorean(book.status)}
                  </Table.Cell>
                </Table.Row>
                  );
                })()
              ) : null
            )
          ) : (
            <Table.Row>
              <Table.Cell textAlign="center" colSpan="8">
                검색결과가 없습니다.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>

        <Table.Footer fullWidth></Table.Footer>
      </Table>
      </div>
      {/* 행 hover 배경 힌트 — 묶음 행의 inline 배경색보다 우선하도록 !important */}
      <style>{`
        .dbrs-book-row:hover { background-color: rgba(0,0,0,.05) !important; }
      `}</style>
    </>
  );
};

export default BookListTable;

function convertDateFormatForMonth(dateStr) {
  let formatedDateStr = `${dateStr.substring(0, 4)}.${dateStr.substring(4, 6)}`;
  return formatedDateStr;
}
